/**
 * Enriched per-platform content for /services/{platform}-account-appeal pages.
 *
 * Each entry expands the bare PLATFORM_DATA in PlatformServicePages.jsx with:
 *  - `whyDisabled`     — detailed reason list with policy mapping (SEO body)
 *  - `appealChannel`   — the official appeal URL Meta/TikTok/etc publish
 *  - `evidenceTypical` — what reviewers typically need (checklist content)
 *  - `faqs`            — 6-10 platform-specific Q&As (FAQ schema target)
 *  - `relatedArticles` — internal links into existing blog posts
 *
 * All content here is sourced from each platform's *published* documentation
 * (Community Guidelines, Help Center, Business Help Center, Oversight Board
 * decisions). No outcome promises, no recovery percentages, no insider claims.
 */

export const PLATFORM_ENHANCEMENTS = {
  instagram: {
    h1Override: "Instagram Account Suspended? Appeal Documentation by Mark Becker",
    metaDescriptionOverride:
      "Instagram account disabled or suspended? Independent appeal documentation service in Honolulu, Hawaii. We map your case to Meta's published Community Guidelines and prepare your appeal package. No guarantees, no insider claims.",
    introCopy:
      "Instagram disables roughly 1.7+ billion fake accounts per quarter — but legitimate accounts get caught in the same automated enforcement. If yours did, the in-app appeal is your first move. If that fails or returns a templated denial, a structured, policy-mapped appeal package is the next escalation.",
    whyDisabled: [
      {
        title: "Community Guidelines violation (most common)",
        body:
          "Automated detection flagged your content under one of Meta's 14 enforcement categories. The notice often doesn't specify which category triggered it. Our reference article maps notice language to the specific category.",
      },
      {
        title: "'Inauthentic behavior' or impersonation",
        body:
          "Includes bulk follow/unfollow patterns, hashtag stuffing, IP-overlap with previously-disabled accounts, or username similarity to public figures. Often appealable if you can document your account's authentic history.",
      },
      {
        title: "Account hacked or compromised",
        body:
          "Meta sometimes preemptively disables accounts when suspicious activity is detected (mass DMs, country-mismatched logins). The recovery path is different from a policy-violation appeal — identity verification is the primary requirement.",
      },
      {
        title: "Business / creator account quality issues",
        body:
          "For business profiles: low feedback scores, customer report patterns, or chargeback signals can trigger account-level enforcement that cascades to linked ad accounts.",
      },
      {
        title: "Intellectual property complaint (DMCA / TM)",
        body:
          "A third party filed a copyright or trademark claim against your content or username. Counter-notice paths exist under Meta's DMCA process.",
      },
      {
        title: "Age verification failure",
        body:
          "Meta requires users to be 13+. If automated systems suspect your account is under-13, a manual verification appeal is required. Government ID is typically necessary.",
      },
      {
        title: "Repeat strikes — pattern of small violations",
        body:
          "Even minor violations accumulate. Three or more 'strikes' within a rolling window can convert a Feature Limit into an account-level disable. Appeals at this stage require addressing the pattern, not just the latest action.",
      },
      {
        title: "Restricted/regulated content",
        body:
          "Cannabis content (even in legal-state US), firearms, certain pharmaceutical content, or sale of restricted goods can trigger disables. Some categories have regional carve-outs.",
      },
    ],
    appealChannel: "https://help.instagram.com/contact/606967319425038",
    evidenceTypical: [
      "Screenshot of the suspension notice (full email including timestamp)",
      "Government-issued photo ID matching the account name",
      "Account creation date and original email address",
      "Most recent post / story / reel timestamps",
      "Documentation of any business legitimacy (LLC, EIN, domain) for creator/business accounts",
      "Any DMCA counter-notice paperwork if IP-related",
    ],
    faqs: [
      {
        q: "How long does an Instagram appeal take?",
        a: "Meta's published response times range from 24 hours to 60 days. Most cases acknowledge within 10 days; final decisions typically arrive 14-30 days after submission. Cases involving identity verification or sensitive policy categories take longer.",
      },
      {
        q: "Will appealing make my Instagram suspension permanent?",
        a: "No. Submitting an appeal does not 'lock in' an enforcement action. Meta's documentation explicitly allows multiple appeals, and a well-prepared second appeal can succeed where a templated first appeal failed.",
      },
      {
        q: "Do you guarantee my Instagram will come back?",
        a: "No — and any service that does is misrepresenting the process. Meta makes the final decision. What we do is prepare the strongest possible appeal package based on Meta's own published policies and your specific account history.",
      },
      {
        q: "Should I create a new account while I wait?",
        a: "Generally no. Meta's systems use device fingerprinting, IP fingerprinting, and identity signals to associate new accounts with disabled ones. A new account can be disabled as 'evading suspension' and can hurt your original appeal's outcome.",
      },
      {
        q: "What's the difference between 'disabled' and 'suspended' on Instagram?",
        a: "Suspended is typically temporary (24h, 7d, 30d feature limits). Disabled means account-level enforcement — you can't log in at all. Banned/permanent means Meta considers the case closed; this is the hardest to overturn.",
      },
      {
        q: "Does Meta Verified help with appeals?",
        a: "Meta Verified subscribers get access to a direct support channel that includes account-restoration support, but only for accounts that were Verified before the disable. You cannot subscribe to Meta Verified on a disabled account.",
      },
      {
        q: "Can I appeal an Instagram suspension after 30 days?",
        a: "Meta's in-app appeal window is 30 days for many enforcement actions, but appeals submitted after that window can still be reviewed through Meta's separate Help Center forms. The 'Request a Review' link expires, but the manual contact form does not.",
      },
      {
        q: "What if my Instagram appeal was already denied?",
        a: "A denied appeal can usually be re-appealed once with new information or a stronger argument. Meta also offers final-review paths for certain account-level actions. We prepare second-round responses at no extra cost for Appeal Package clients.",
      },
    ],
    relatedArticles: [
      { slug: "instagram-community-guidelines-violations-reference", title: "Instagram Community Guidelines: Complete Reference" },
      { slug: "instagram-business-account-suspended-what-to-do", title: "Instagram Business Account Suspended: What To Do" },
      { slug: "how-long-does-instagram-appeal-take", title: "How Long Does an Instagram Appeal Take?" },
      { slug: "hacked-instagram-account-what-to-do", title: "Hacked Instagram Account? Recovery Steps" },
    ],
  },

  facebook: {
    h1Override: "Facebook Account Disabled? Appeal Documentation by Mark Becker",
    metaDescriptionOverride:
      "Facebook account or Business Manager disabled? Independent appeal-documentation service in Honolulu, Hawaii. We map your case to Meta's Community Standards and Advertising Standards. No guarantees.",
    introCopy:
      "Facebook account disables fall into two very different buckets: personal-account disables (Community Standards) and Business Manager / ad-account disables (Advertising Standards + Account Quality). The appeal paths, evidence requirements, and timelines are different. Below is what each looks like.",
    whyDisabled: [
      {
        title: "Community Standards violation",
        body:
          "Personal accounts: hate speech, bullying, violence/incitement, fraud, or one of Meta's other published categories. Automated detection is the most common trigger.",
      },
      {
        title: "Advertising Standards violation (ad accounts)",
        body:
          "Personal-attribute targeting, misleading claims, restricted-category ads (financial, health, political), or ad-cloaking. Often cascades to the Business Manager that owns the ad account.",
      },
      {
        title: "Account Quality / customer feedback signals",
        body:
          "Business accounts with shipping operations: customer feedback scores below 2.5/5, high 'hide ad' / 'report ad' rates, or excessive returns can trigger restrictions and ultimately disables.",
      },
      {
        title: "Identity verification failure",
        body:
          "Meta sometimes requires Business Verification or personal identity verification. Three failed submissions can trigger an account-level disable.",
      },
      {
        title: "Connected-account enforcement (cascade)",
        body:
          "If another Business Manager you're admin/employee on gets disabled, *your* BM can be flagged. Agencies are especially exposed to this.",
      },
      {
        title: "Suspicious login / hacked account",
        body:
          "Country-mismatched logins, mass messaging, or sudden permission changes trigger a preemptive disable. The recovery path requires identity verification.",
      },
      {
        title: "Page unpublished (separate from account disable)",
        body:
          "A Page can be unpublished without disabling the underlying personal account. The appeal is filed through the Page's own settings — different form, different reviewers.",
      },
      {
        title: "Marketplace / Commerce policy violations",
        body:
          "Sale of restricted goods, listing-quality issues, or buyer-complaint patterns can disable Marketplace access while leaving the rest of the account intact.",
      },
    ],
    appealChannel: "https://www.facebook.com/help/contact/260749603972907",
    evidenceTypical: [
      "Screenshot of the disable notification (full Meta email)",
      "Government-issued photo ID matching the account name",
      "LLC / corporation paperwork (for business cases)",
      "EIN letter (for business cases)",
      "Domain ownership documentation (for advertised domains)",
      "Most recent ad account billing statement (for ad cases)",
      "Documentation of any chargeback resolutions",
    ],
    faqs: [
      {
        q: "How long does a Facebook appeal take?",
        a: "Personal accounts: 24 hours to 30 days for most cases. Business Manager / ad account cases: 3-14 days for first response, 21-90 days end-to-end. Pro Help (Meta Verified for Business) has a 24-48 hour SLA.",
      },
      {
        q: "Why was my Facebook disabled without warning?",
        a: "Meta's automated systems can trigger account-level enforcement without a prior Feature Limit if the cited category is high-severity (CSAM, terrorism, fraud, CIB). For most other categories, Feature Limits come first.",
      },
      {
        q: "Can I get my Business Manager back if it's permanently disabled?",
        a: "Meta's documentation states most BM disables can be appealed, but the success rate depends heavily on the category. CIB and severe Advertising Standards violations have very low reversal rates. Account-quality and identity-verification disables have higher rates.",
      },
      {
        q: "What happens to my ad spend when my account is disabled?",
        a: "Pending campaigns stop immediately. Outstanding balances remain owed. Refunds for pre-paid balances are case-by-case — Meta's Help Center documents the request process.",
      },
      {
        q: "Should I create a new Facebook account to keep advertising?",
        a: "No. Meta's connected-account systems will detect the new account and disable it as 'evading suspension.' This typically also damages your original appeal's outcome.",
      },
      {
        q: "What if my Business Manager was disabled because of a client account?",
        a: "This is the connected-account cascade scenario. Meta has a specific 'Connection Review' appeal path that requests they re-evaluate the connection between your BM and the disabled client BM. Documenting your agency relationship is critical.",
      },
      {
        q: "Is the official Facebook appeal form different for businesses?",
        a: "Yes. Personal accounts use the standard 'Disabled Account' form. Business Manager and ad-account cases use the Business Help Center, which routes to Meta's Business Integrity team — different reviewers with deeper policy training.",
      },
      {
        q: "Do you handle Facebook Page unpublished appeals?",
        a: "Yes. Page unpublishes are appealed through the Page's own Settings → Page Quality → 'Request Review.' We help prepare the supporting documentation regardless of which appeal form your case uses.",
      },
    ],
    relatedArticles: [
      { slug: "facebook-business-manager-disabled-complete-reference", title: "Facebook BM Disabled: Complete Reference" },
      { slug: "how-to-appeal-disabled-facebook-account-2025", title: "How to Appeal a Disabled Facebook Account" },
      { slug: "worst-mistakes-social-media-account-appeal", title: "7 Worst Mistakes in Account Appeals" },
    ],
  },

  tiktok: {
    h1Override: "TikTok Account Banned? Appeal Documentation by Mark Becker",
    metaDescriptionOverride:
      "TikTok account banned or restricted? Independent appeal documentation service in Honolulu, Hawaii. We map your case to TikTok's Community Guidelines and prepare your appeal. No guarantees, no insider claims.",
    introCopy:
      "TikTok's enforcement system is heavier on automation than Meta's — meaning misclassifications are more common, but well-documented appeals also have a measurable response. The in-app appeal is your first move; if that returns a generic denial, a documented appeal via TikTok's support email is the next step.",
    whyDisabled: [
      {
        title: "Community Guidelines violation",
        body:
          "TikTok's published Community Guidelines have 13 enforcement categories. Most disables cite one of them in the notice. Pattern-matching the notice language to the category drives the appeal strategy.",
      },
      {
        title: "Multiple violations / strike accumulation",
        body:
          "TikTok uses a strike system. Hitting strike threshold (typically 3+ within a rolling 30-day window) converts a Feature Limit into an account-level ban.",
      },
      {
        title: "Minor safety concerns",
        body:
          "TikTok's age threshold is 13 globally. Accounts suspected of being underage are disabled pending age verification. Government ID is usually required.",
      },
      {
        title: "Spam, fake engagement, or platform manipulation",
        body:
          "Mass follow/unfollow patterns, comment-bot use, view-inflation services, or IP-overlap with previously-banned accounts. Documenting authentic engagement history is the path forward.",
      },
      {
        title: "Creator Fund / monetization eligibility loss",
        body:
          "Separate from account-level enforcement: Creator Fund eligibility can be revoked for content quality issues, low watch-through rates, or community-guidelines warnings that didn't rise to account-disable level.",
      },
      {
        title: "LIVE access revoked",
        body:
          "LIVE has its own policy stack (LIVE Code of Conduct). Account can stay active while LIVE access is restricted. Appeal is through the LIVE-specific form.",
      },
      {
        title: "Intellectual property / trademark complaint",
        body:
          "Third-party DMCA or TM claim. Counter-notice path is documented in TikTok's IP Help Center. Resolving an IP claim is often the fastest disable-reversal path.",
      },
      {
        title: "Hacked / compromised account",
        body:
          "TikTok's recovery path requires phone + email verification. If the hacker changed both, the only path is TikTok's account-ownership verification form (with video selfie + ID).",
      },
    ],
    appealChannel: "https://www.tiktok.com/legal/report/feedback",
    evidenceTypical: [
      "Screenshot of the ban notice (full notification + any in-app messages)",
      "Government-issued photo ID matching the account name",
      "TikTok username (without @) and original phone/email",
      "Account creation date (approximate)",
      "List of any recent strikes or warnings received",
      "Documentation of authentic engagement (real followers, real content history)",
    ],
    faqs: [
      {
        q: "How long does a TikTok appeal take?",
        a: "In-app appeals: 24-72 hours for most cases. Email appeals through TikTok's support address: 7-21 days. LIVE-specific appeals: 24-48 hours. Cases involving identity verification can extend to 30+ days.",
      },
      {
        q: "Can I appeal a permanent TikTok ban?",
        a: "TikTok's documentation states permanent bans are appealable through their support email channel, but the success rate is meaningfully lower than for temporary restrictions. The 'final review' path requires a structured argument tied to a specific policy section.",
      },
      {
        q: "Why was my TikTok banned with no warning?",
        a: "TikTok can trigger account-level enforcement without prior strikes for high-severity categories (Minor Safety, CSAM, Violent Extremism, certain Hate Speech tiers). Most other categories require strike accumulation first.",
      },
      {
        q: "Should I create a new TikTok while waiting for my appeal?",
        a: "No. TikTok uses device fingerprinting, phone-number reuse detection, and IP signals to associate new accounts with banned ones. A new account from the same device is typically banned within hours and damages the original appeal.",
      },
      {
        q: "Will appealing reset my TikTok strikes?",
        a: "A successful appeal removes the associated strike. A denied appeal leaves the strike in place. Strike-removal appeals are separate from account-level disable appeals.",
      },
      {
        q: "What if TikTok wants me to do a video selfie verification?",
        a: "This is TikTok's standard account-ownership verification flow for hacked-account recovery. The video must match the ID you submit and the face on the original account. Reused or AI-generated videos are detected and result in denial.",
      },
      {
        q: "Can I get my TikTok LIVE access back if I lost it?",
        a: "Yes — LIVE access is appealable through TikTok's LIVE-specific feedback form. The appeal must address the specific cited LIVE Code of Conduct section.",
      },
      {
        q: "Does TikTok ban for monetization fraud?",
        a: "Yes. View-inflation, bot engagement, Creator Fund manipulation, and ad-revenue fraud can result in account-level bans plus retroactive earnings clawback. These are among the hardest categories to appeal.",
      },
    ],
    relatedArticles: [
      { slug: "tiktok-ban-appeal-step-by-step-guide", title: "TikTok Ban Appeal: Step-by-Step Guide" },
      { slug: "worst-mistakes-social-media-account-appeal", title: "7 Worst Mistakes in Account Appeals" },
    ],
  },

  youtube: {
    h1Override: "YouTube Channel Terminated? Appeal Documentation by Mark Becker",
    metaDescriptionOverride:
      "YouTube channel terminated, demonetized, or struck? Independent appeal-documentation service in Honolulu, Hawaii. We map your case to YouTube's Community Guidelines and Terms of Service.",
    introCopy:
      "YouTube terminations are some of the highest-stakes platform enforcement actions — losing a monetized channel can mean losing years of revenue. YouTube's appeal path is specific (single appeal form, often single-shot). A well-documented first appeal is critical because second appeals are not always available.",
    whyDisabled: [
      {
        title: "Community Guidelines strikes (3-strike rule)",
        body:
          "YouTube's strike system: 3 active strikes within 90 days = channel termination. Each strike must be appealed individually through the Strike Appeal form within 60 days.",
      },
      {
        title: "Copyright strikes (separate from Community Guidelines)",
        body:
          "3 copyright strikes within 90 days = termination. Counter-notification is filed through YouTube's Copyright Center, then the original claimant has 10-14 business days to escalate to legal action.",
      },
      {
        title: "Terms of Service violation (single-shot termination)",
        body:
          "Some categories trigger immediate termination without strikes: CSAM, terrorism, severe harassment, account creation circumvention. These are the hardest to appeal.",
      },
      {
        title: "Spam, scams, or deceptive practices",
        body:
          "Includes view-inflation, fake engagement, misleading metadata, scam-pattern content. Often results in monetization suspension first, then termination if patterns continue.",
      },
      {
        title: "Monetization suspended (separate from channel termination)",
        body:
          "YPP membership can be revoked while the channel stays active. Appeals go through the YPP-specific review form in YouTube Studio.",
      },
      {
        title: "AdSense account issues",
        body:
          "AdSense and YouTube are separate accounts even though they're linked. AdSense termination disables YouTube monetization but doesn't necessarily terminate the channel. Different appeal channel (AdSense Help Forum + appeal form).",
      },
      {
        title: "Account hacked / compromised",
        body:
          "Google account recovery, not YouTube-specific. Path: accounts.google.com/recovery. If the channel was harmed by the hacker, YouTube has a 'restore my channel' form after the Google account is recovered.",
      },
      {
        title: "Repeat-offender enforcement (channel reincarnation)",
        body:
          "If YouTube detects a new channel from a terminated user, the new channel is terminated immediately as 'circumvention.' This is one of the lowest-appeal-success categories.",
      },
    ],
    appealChannel: "https://support.google.com/youtube/contact/tossla",
    evidenceTypical: [
      "Channel URL and Channel ID (Studio → Settings → Channel)",
      "Screenshots of the termination notice + any strike notifications",
      "Government-issued photo ID matching the account name",
      "Most recent monetization status / YPP eligibility",
      "Copyright claim details if applicable (Content ID claim numbers)",
      "Documentation of fair-use arguments if relevant",
      "AdSense Publisher ID if monetization-related",
    ],
    faqs: [
      {
        q: "How long does a YouTube termination appeal take?",
        a: "Most appeals receive a response within 7-21 days. Copyright counter-notifications follow a fixed legal timeline: 10-14 business days for the claimant to respond, then YouTube auto-restores if no escalation.",
      },
      {
        q: "Can I appeal a YouTube termination more than once?",
        a: "YouTube's official guidance is that each termination has a single appeal opportunity. In practice, additional submissions via support channels are possible but the success rate drops significantly after the first denial.",
      },
      {
        q: "What's the difference between a strike and a termination?",
        a: "A strike is a warning attached to a specific video. Three active strikes = termination. Strikes expire after 90 days. Terminations are channel-level and apply across all videos and revenue.",
      },
      {
        q: "Will appealing copyright strikes hurt my channel?",
        a: "Counter-notifications are formal legal documents. Filing a counter-notice in bad faith can expose you to liability. We help structure counter-notices based on fair-use criteria or licensed-content evidence — never on misrepresentation.",
      },
      {
        q: "Can I create a new YouTube channel after termination?",
        a: "YouTube's Terms of Service prohibit creating new channels after termination. New channels detected as 'circumvention' are typically terminated within days. This is also one of the only behaviors that can permanently bar future appeals.",
      },
      {
        q: "How do I appeal if my AdSense was terminated separately?",
        a: "AdSense termination is appealed through the AdSense Help → 'Appeal Account Suspension' form. The appeal must address Google's invalid-traffic policies specifically, not YouTube's Community Guidelines.",
      },
      {
        q: "What if my channel was hacked and the hacker uploaded violations?",
        a: "Recover the underlying Google account first (accounts.google.com/recovery). Once recovered, YouTube has a 'restore my channel' path for hacked-account scenarios where the violations occurred during the hack window.",
      },
      {
        q: "Does YouTube Premium / membership help with appeals?",
        a: "No. YouTube Premium is a viewer-side subscription with no creator-side support tier. Some YouTube Partner Program members get priority support for monetization issues, but not for termination appeals.",
      },
    ],
    relatedArticles: [
      { slug: "youtube-channel-terminated-appeal-options", title: "YouTube Channel Terminated: Appeal Options" },
      { slug: "worst-mistakes-social-media-account-appeal", title: "7 Worst Mistakes in Account Appeals" },
    ],
  },

  twitter: {
    h1Override: "X / Twitter Account Suspended? Appeal Documentation by Mark Becker",
    metaDescriptionOverride:
      "X (formerly Twitter) account suspended or restricted? Independent appeal-documentation service in Honolulu, Hawaii. We map your case to X's Rules and prepare your appeal package.",
    introCopy:
      "X's enforcement system has changed significantly since the 2022 ownership change — the Rules are shorter, automated detection is heavier, and the appeal path is now a single form (Appeals Center). Locked accounts (most common) are often resolvable; permanent suspensions are harder.",
    whyDisabled: [
      {
        title: "Platform manipulation / spam",
        body:
          "Mass follow/unfollow, identical-text posting, hashtag abuse, or IP overlap with previously-suspended accounts. Most common suspension category for new accounts.",
      },
      {
        title: "Abusive behavior / hateful conduct",
        body:
          "Targeted harassment, slurs, calls for violence. X's Hateful Conduct policy is narrower than Meta's but enforced more aggressively on direct mentions.",
      },
      {
        title: "Account locked (security trigger)",
        body:
          "Country-mismatched login, suspicious login patterns, or login from a flagged IP. Resolution is usually phone-number verification — different path from a policy suspension.",
      },
      {
        title: "Verification / authenticity issues",
        body:
          "Impersonation of public figures or brands, undisclosed parody accounts, or paid verification (X Premium) on accounts whose identity doesn't match payment information.",
      },
      {
        title: "Evading a prior suspension",
        body:
          "Creating a new X account after a permanent suspension. X's systems detect this through device fingerprinting and IP history. Among the hardest categories to appeal.",
      },
      {
        title: "API / automation policy violation",
        body:
          "Unauthorized scraping, undisclosed bot operation, or use of paid view-inflation services. Often results in account-level suspension plus API key revocation for developers.",
      },
      {
        title: "Sensitive media policy",
        body:
          "Posting graphic violence, NSFW content without media flag, or content depicting victims of violence. Often results in account restriction (rate limits, search hidden) rather than full suspension.",
      },
      {
        title: "Copyright / DMCA strikes",
        body:
          "Counter-notice path documented in X's IP Policy. Repeat DMCA violations result in account suspension with separate appeal route.",
      },
    ],
    appealChannel: "https://help.x.com/forms/general?subtopic=suspended",
    evidenceTypical: [
      "Screenshot of the suspension notice (and any 'Appeal' link)",
      "Government-issued photo ID matching the account name",
      "Original phone number linked to the account",
      "Approximate account creation date",
      "Username (without @) and any prior usernames",
      "Documentation of any X Premium / Blue / Verified history",
    ],
    faqs: [
      {
        q: "How long does an X (Twitter) appeal take?",
        a: "Most appeals receive a response within 1-7 days. Locked-account resolutions are usually same-day (phone verification). Permanent suspension appeals can take 14-30 days and have lower response rates than other platforms.",
      },
      {
        q: "What's the difference between locked and suspended on X?",
        a: "Locked = security-driven, resolved through verification (phone, email, behavior challenge). Suspended = policy-driven, requires appeal. Permanently suspended = X considers the case closed; very low overturn rate.",
      },
      {
        q: "Does X Premium / Verified status help with appeals?",
        a: "X Premium subscribers technically have access to prioritized review but the system's behavior has been inconsistent post-2022. The subscription itself doesn't make appeals more likely to succeed — the policy citation does.",
      },
      {
        q: "Why does X say 'evading suspension' on my new account?",
        a: "X's systems associated your new account with a previously-suspended account through device fingerprinting, IP overlap, or phone number reuse. The only path forward is appealing the original suspension, not the new account.",
      },
      {
        q: "Can I appeal an X suspension after 6 months?",
        a: "Yes. X's appeal form doesn't have a time limit. Older cases sometimes get more thorough human review since the automated 'recent enforcement' queue is bypassed. Documentation is critical for older appeals.",
      },
      {
        q: "What about API key suspensions vs. account suspensions on X?",
        a: "These are separate. Developer API key suspension is appealed through X's Developer Platform support, not the general user appeal form. Account-level suspensions also revoke associated API access.",
      },
      {
        q: "Will deleting tweets help my X appeal?",
        a: "Generally no, and sometimes hurts. The platform retains records of removed content; deleting after a violation can look like evidence-tampering. Better to acknowledge in the appeal that the content existed and explain context.",
      },
    ],
    relatedArticles: [
      { slug: "worst-mistakes-social-media-account-appeal", title: "7 Worst Mistakes in Account Appeals" },
    ],
  },

  linkedin: {
    h1Override: "LinkedIn Account Restricted? Appeal Documentation by Mark Becker",
    metaDescriptionOverride:
      "LinkedIn account restricted, limited, or banned? Independent appeal-documentation service. We map your case to LinkedIn's Professional Community Policies and prepare structured appeals.",
    introCopy:
      "LinkedIn restrictions are different from typical social-platform suspensions: they're often connection-limit-driven, automation-driven, or tied to Recruiter/Sales Navigator accounts. The appeal path runs through LinkedIn Help Center and, for premium accounts, through dedicated support.",
    whyDisabled: [
      {
        title: "Connection-request limits exceeded",
        body:
          "LinkedIn enforces hidden weekly/monthly limits. Exceeding them — especially with high reject rates — triggers a 'Restricted' status until appeal.",
      },
      {
        title: "Professional Community Policies violation",
        body:
          "Spam DMs, scam content, irrelevant promotional posting, or harassment. Often results in account restriction rather than full ban.",
      },
      {
        title: "Sales Navigator / Recruiter account suspension",
        body:
          "Paid account suspensions follow a separate path. LinkedIn Premium support is involved. Appeal can include billing dispute if account was paid through the suspension period.",
      },
      {
        title: "Identity / authenticity flag",
        body:
          "Profile flagged as potentially fake based on photo, work history, or behavior signals. Government ID typically required to lift restriction.",
      },
      {
        title: "Third-party automation tool use",
        body:
          "Using browser extensions, scrapers, or automation tools violates LinkedIn's Terms. Detection is sophisticated (mouse movement, timing patterns). Account-level restriction is common.",
      },
      {
        title: "Content policy violation",
        body:
          "Hate speech, misinformation in posts, or content violating LinkedIn's professional standards. Typically content removal first; account-level enforcement for patterns.",
      },
    ],
    appealChannel: "https://www.linkedin.com/help/linkedin/answer/a1336988",
    evidenceTypical: [
      "Screenshot of the restriction notice",
      "Government-issued photo ID matching profile name",
      "Work history documentation (resume, employer email verification)",
      "Premium subscription receipts if applicable",
      "Documentation of any automation tools removed",
    ],
    faqs: [
      {
        q: "How long do LinkedIn restrictions last?",
        a: "Connection-limit restrictions: typically 24h-7 days, resolved automatically. Policy-driven restrictions: appeal required, 7-21 days. Premium account restrictions: 14-30 days with support involvement.",
      },
      {
        q: "Can I appeal a LinkedIn ban for using automation?",
        a: "Yes, but the appeal must demonstrate the tool has been removed and won't be reused. Repeat automation violations typically lead to permanent restriction.",
      },
      {
        q: "Does LinkedIn Premium help with appeals?",
        a: "Premium members get access to faster customer support and a dedicated case path. The Premium subscription itself doesn't reduce policy enforcement, but it accelerates response time.",
      },
      {
        q: "What if I'm restricted from sending connection requests?",
        a: "This is typically a soft restriction that resolves on its own after 1-2 weeks. If it persists or shows a 'Restricted' badge on your profile, an explicit appeal is needed.",
      },
    ],
    relatedArticles: [
      { slug: "worst-mistakes-social-media-account-appeal", title: "7 Worst Mistakes in Account Appeals" },
    ],
  },

  ubereats: {
    h1Override: "Uber Eats Account Deactivated? Appeal Documentation by Mark Becker",
    metaDescriptionOverride:
      "Uber Eats driver or restaurant account deactivated? Independent appeal-documentation service. We help prepare structured appeals citing Uber's published policies.",
    introCopy:
      "Uber Eats account deactivations affect both drivers and restaurant partners — and the appeal paths differ significantly. Drivers go through the Uber Driver Help portal; restaurants go through Uber Eats Manager support. Background-check disputes have their own escalation path.",
    whyDisabled: [
      {
        title: "Low driver/restaurant ratings or customer complaints",
        body:
          "Sustained ratings below Uber's threshold (typically 4.6 for drivers) or pattern of complaints can trigger deactivation. Appeal requires demonstrating context and improvement plan.",
      },
      {
        title: "Background-check concerns (drivers)",
        body:
          "Mid-employment background-check updates can disable accounts. Disputed through Checkr or similar provider, then through Uber's reinstatement form.",
      },
      {
        title: "Fraud allegations",
        body:
          "Account sharing, fake deliveries, GPS spoofing, or referral fraud. High-severity; harder to appeal but documentation of legitimate activity can succeed.",
      },
      {
        title: "Policy violations (community guidelines)",
        body:
          "Verbal abuse complaints, refusal to deliver, or safety issues. Appeal must address the specific cited incident with context.",
      },
      {
        title: "Documentation / verification failure",
        body:
          "Expired insurance, driver's license, or vehicle registration. Easiest category to resolve — provide updated documents.",
      },
    ],
    appealChannel: "https://help.uber.com/driving-and-delivering",
    evidenceTypical: [
      "Screenshot of the deactivation notice",
      "Government-issued photo ID",
      "Current insurance / registration / license (for drivers)",
      "EIN + business license (for restaurants)",
      "Documentation of incidents cited (dashcam, photos, communication records)",
      "Background-check dispute confirmation if applicable",
    ],
    faqs: [
      {
        q: "How long does an Uber Eats deactivation appeal take?",
        a: "Documentation-related deactivations: 24-72 hours once documents are submitted. Policy-driven deactivations: 7-21 days. Background-check disputes: 14-30 days through Checkr's process.",
      },
      {
        q: "Can I get permanently deactivated from Uber Eats?",
        a: "Some categories (fraud, safety violations, repeat policy violations) result in permanent deactivation with very limited appeal options. Single-incident deactivations have higher appeal success rates.",
      },
      {
        q: "Does deactivation affect my Uber rideshare account?",
        a: "Sometimes. Driver deactivations often cross-apply between Uber and Uber Eats. Restaurant deactivations are platform-specific.",
      },
    ],
    relatedArticles: [
      { slug: "worst-mistakes-social-media-account-appeal", title: "7 Worst Mistakes in Account Appeals" },
    ],
  },

  'google-business': {
    h1Override: "Google Business Profile Suspended? Appeal Documentation by Mark Becker",
    metaDescriptionOverride:
      "Google Business Profile suspended? Independent reinstatement-documentation service. We map your case to Google's published Business Profile guidelines.",
    introCopy:
      "Google Business Profile suspensions are some of the costliest local-search enforcement actions — losing your GBP listing can mean instant disappearance from Google Maps, local-pack rankings, and direct customer calls. Google's reinstatement path is specific: a single form, evidence-driven, often requiring video verification or postcard verification.",
    whyDisabled: [
      {
        title: "Business model or category considered ineligible",
        body:
          "Some categories (locksmiths, garage-door repair, addiction-treatment, certain financial services) face heightened scrutiny under Google's anti-spam policies. Verification of physical address + license documentation is critical.",
      },
      {
        title: "Address verification failure",
        body:
          "Virtual-office, mailbox, or coworking-space addresses are routinely flagged. Service-area-business (SAB) restructuring without a public-facing address may be the path forward.",
      },
      {
        title: "Duplicate listing detection",
        body:
          "Multiple GBP listings for the same business — even by previous owners — can suspend the active listing. Requires consolidation appeal.",
      },
      {
        title: "Keyword stuffing in business name",
        body:
          "Adding keywords to the business name field (e.g., 'Joe's Plumbing — 24/7 Emergency Plumber Near Me') violates Google's guidelines. Reverting to the legal business name resolves most of these.",
      },
      {
        title: "Service-area boundary issues",
        body:
          "SAB listings that claim service areas larger than 2 hours of driving from the business location, or overlapping with other listings, trigger automatic suspension.",
      },
      {
        title: "Suspicious review patterns",
        body:
          "Reviews from gift-card incentivized customers, fake reviews, or reviews from IPs matching the business owner trigger pattern detection.",
      },
      {
        title: "Multi-location operator flags",
        body:
          "Franchise locations, chains, and multi-location owners face elevated review thresholds. Bulk-verification documentation is needed.",
      },
      {
        title: "Category mismatch",
        body:
          "Primary category not matching the business's actual operations (verified via website, signage, license) is a frequent first-suspension trigger.",
      },
    ],
    appealChannel: "https://support.google.com/business/contact/business_redressal_form",
    evidenceTypical: [
      "Business license / permit / occupational license documentation",
      "Lease or property ownership documentation (for the listed address)",
      "Tax document (EIN letter, state tax ID) matching business name",
      "Storefront photos showing signage matching business name (for storefront businesses)",
      "Vehicle wrap photos matching business name (for service-area businesses)",
      "Insurance certificate matching business name and address",
      "Utility bill in business name at the listed address",
    ],
    faqs: [
      {
        q: "How long does a Google Business Profile reinstatement take?",
        a: "Standard reinstatement appeals: 3-14 business days. Cases requiring video verification: 5-21 business days. Hard-suspension cases (multiple prior violations) can extend to 30+ days.",
      },
      {
        q: "What's the difference between 'soft' and 'hard' Google Business Profile suspension?",
        a: "Soft suspension: profile still visible but cannot be edited; usually resolved with verification documentation. Hard suspension: profile removed from Maps and Search; requires full reinstatement appeal.",
      },
      {
        q: "Can I create a new Google Business Profile after suspension?",
        a: "No. Google's systems detect new profiles for the same business and apply the same suspension. The only path is reinstating the original profile.",
      },
      {
        q: "Does Google require video verification for every appeal?",
        a: "Increasingly common but not universal. Google asks for video verification when address evidence is borderline. The video must show: business signage, surrounding street, and proof of operation (e.g., walking from the street to the door, showing inside).",
      },
      {
        q: "What if I changed my business name and Google suspended the listing?",
        a: "Name changes are flagged automatically. The fix is to submit a name-change appeal with updated legal documentation (DBA, articles of amendment, updated license).",
      },
    ],
    relatedArticles: [
      { slug: "worst-mistakes-social-media-account-appeal", title: "7 Worst Mistakes in Account Appeals" },
    ],
  },

  'facebook-business-manager': {
    h1Override: "Facebook Business Manager Disabled? Appeal Documentation by Mark Becker",
    metaDescriptionOverride:
      "Facebook Business Manager disabled? Independent appeal-documentation service for the cascading disable across ad accounts, Pages, pixels, catalogs, and WhatsApp Business API.",
    introCopy:
      "Facebook Business Manager (BM) disables are different from personal-account disables: a single BM action cascades across every ad account, Page, pixel, catalog, and WhatsApp Business API account in your portfolio simultaneously. Understanding the cascade is critical to scoping the appeal correctly. We cover this in-depth in our published reference article — this service page is for businesses ready to take action.",
    whyDisabled: [
      {
        title: "Advertising Standards violation (most common)",
        body:
          "Personal-attribute targeting, misleading claims, restricted-category content, or ad cloaking. Often cascades from a single ad account to the entire BM.",
      },
      {
        title: "Account Quality / feedback signals",
        body:
          "Customer feedback scores below 2.5/5 (for shipping businesses), high 'hide ad' rates, or excessive returns. Quality issues compound over multiple ad accounts.",
      },
      {
        title: "Business Verification failure",
        body:
          "Three failed business-verification submissions trigger BM-level enforcement. Common causes: name mismatch between BM and legal entity, missing domain verification, payment method mismatch.",
      },
      {
        title: "Connected-account cascade enforcement",
        body:
          "If another BM you're admin/employee on is disabled, your BM can be flagged. Agencies are especially exposed. Documentation of the relationship is critical to break the connection.",
      },
      {
        title: "Coordinated Inauthentic Behavior (CIB) flag",
        body:
          "Multi-account fingerprinting, IP overlap with previously-disabled BMs, or sockpuppet patterns. Lowest appeal-success category — often requires legal escalation.",
      },
      {
        title: "Repeat policy violations",
        body:
          "Pattern of small violations across linked Pages or ad accounts accumulates into BM-level enforcement, even if no single violation would have triggered it.",
      },
      {
        title: "Domain verification disputes",
        body:
          "Domain Verification claimed by another party can preemptively disable your BM. Resolving the domain claim is usually a prerequisite.",
      },
      {
        title: "Billing or chargeback issues",
        body:
          "Repeated payment failures, large chargebacks, or payment method mismatch with BM country can trigger billing-side enforcement separate from policy enforcement.",
      },
    ],
    appealChannel: "https://www.facebook.com/business/help/contact/289407004559500",
    evidenceTypical: [
      "Screenshot of the BM disable notification (full Meta email + timestamp)",
      "Government-issued photo ID of the BM admin",
      "LLC / corporation paperwork (Articles of Organization)",
      "EIN letter from the IRS",
      "Domain ownership documentation (registrar receipts for advertised domains)",
      "Most recent ad account billing statement",
      "List of all assets cascaded into the disable (ad accounts, Pages, pixels, catalogs, WhatsApp)",
      "For agencies: documentation of client relationships and authorized work",
    ],
    faqs: [
      {
        q: "How long does a Facebook Business Manager appeal take?",
        a: "3-14 days for initial response. 21-90 days end-to-end for most cases. Cases with Pro Help (Meta Verified for Business) have a 24-48 hour SLA for first response.",
      },
      {
        q: "Can I get my Business Manager back if disabled for CIB?",
        a: "Coordinated Inauthentic Behavior is among the lowest appeal-success categories. Appeals rarely succeed when Meta has multi-account fingerprinting evidence. The strongest path is often a legal documentation channel rather than a standard appeal.",
      },
      {
        q: "What happens to my ad spend when my BM is disabled?",
        a: "Pending campaigns stop immediately. Outstanding balances remain owed. Refunds for pre-paid balances are case-by-case — Meta's Help Center documents the request process.",
      },
      {
        q: "Should I create a new Business Manager while I wait?",
        a: "No. Meta's connected-account systems detect and disable new BMs from the same admin/device/IP. New BMs typically get disabled within hours and damage the original appeal.",
      },
      {
        q: "What if my BM was disabled because of a client's account?",
        a: "Connected-account cascade. Meta has a specific 'Connection Review' appeal path requesting re-evaluation of the connection. Documenting your agency relationship and showing you've removed access to the offending client is critical.",
      },
      {
        q: "Is Pro Help worth it for a disabled BM?",
        a: "Only if the BM had an active Meta Verified for Business subscription before the disable. You cannot subscribe Pro Help to a disabled BM.",
      },
    ],
    relatedArticles: [
      { slug: "facebook-business-manager-disabled-complete-reference", title: "Facebook BM Disabled: Complete Reference (Deep Dive)" },
      { slug: "how-to-appeal-disabled-facebook-account-2025", title: "How to Appeal a Disabled Facebook Account" },
    ],
  },

  'instagram-ad-account': {
    h1Override: "Instagram Ad Account Disabled? Appeal Documentation by Mark Becker",
    metaDescriptionOverride:
      "Instagram ad account disabled or restricted? Independent appeal-documentation service citing Meta's Advertising Standards. Independent service, no guarantees.",
    introCopy:
      "Instagram ad accounts are governed by Meta's Advertising Standards (separate from Instagram Community Guidelines) plus the Account Quality framework. A disabled IG ad account often signals deeper issues — it can cascade to the linked Business Manager or be a symptom of an underlying BM-level enforcement action.",
    whyDisabled: [
      {
        title: "Personal-attribute targeting violation",
        body:
          "Ads that imply Meta knows the viewer's race, religion, sexuality, medical condition, or financial status. Most common first-violation category for new advertisers.",
      },
      {
        title: "Misleading or unverified claims",
        body:
          "Before/after photos, 'guaranteed results' language, fake testimonials, before/after weight-loss imagery. Health and finance categories are heavily enforced.",
      },
      {
        title: "Restricted-category content",
        body:
          "Financial services, alcohol, pharmaceuticals, political/social-issue advertising — all require pre-approval or special verification. Running without verification triggers disable.",
      },
      {
        title: "Account quality / high disapproval rate",
        body:
          "A pattern of ad disapprovals (typically >20% rolling 30-day rate) triggers account-level enforcement separate from any specific ad violation.",
      },
      {
        title: "Linked Business Manager cascade",
        body:
          "If the parent BM has any pending enforcement action, the IG ad account can be preemptively disabled even if its own ads were clean.",
      },
      {
        title: "Billing failure or chargeback signals",
        body:
          "Repeated card declines, chargebacks, or payment-method mismatch can trigger ad-account-level disable separate from policy issues.",
      },
      {
        title: "Pixel / Conversion API data mismatch",
        body:
          "Pixel firing from non-verified domains, missing aggregated event measurement setup (iOS 14.5+), or anomalous event patterns can flag the account.",
      },
      {
        title: "Domain ownership conflict",
        body:
          "Trying to advertise a domain that another party has claimed via Domain Verification can preemptively disable your ad account.",
      },
    ],
    appealChannel: "https://www.facebook.com/business/help/contact/2025456854280468",
    evidenceTypical: [
      "Screenshot of the ad-account disable notification",
      "Government-issued photo ID matching the ad-account owner",
      "Business documentation (LLC / EIN / business license)",
      "Domain ownership documentation (registrar receipts for advertised domains)",
      "Most recent ad-account billing statement",
      "Pre-disable ad creative library (screenshots of the actual ads that ran)",
      "Resolution of any chargebacks or billing disputes",
    ],
    faqs: [
      {
        q: "How long does an Instagram ad account appeal take?",
        a: "3-14 days for first response. 21-60 days end-to-end. The same Meta Business Integrity team handles IG ad-account appeals as Facebook ad-account appeals.",
      },
      {
        q: "Is my Instagram personal account affected when my ad account is disabled?",
        a: "Usually no — IG ad-account disables typically don't cascade to personal accounts. The reverse is more common: personal-account issues cascade into ad-account access.",
      },
      {
        q: "Why was my new Instagram ad account disabled within minutes?",
        a: "Most commonly: device/IP fingerprinting flagged you as 'evading suspension' from a previously-disabled account. Or: payment-method mismatch with the BM country. Or: linked to a flagged BM.",
      },
      {
        q: "Can I keep advertising on Facebook if my Instagram ads are disabled?",
        a: "If the disable is at the IG-ad-account level only (not BM-wide), Facebook ad delivery from the same BM may continue. If the BM is disabled, both platforms stop simultaneously.",
      },
      {
        q: "Should I dispute the chargeback to recover my ad account?",
        a: "If the chargeback was customer-initiated (not your action), resolving it favorably with the bank is a strong positive signal. Document the resolution and include it in your appeal.",
      },
    ],
    relatedArticles: [
      { slug: "instagram-community-guidelines-violations-reference", title: "Instagram Community Guidelines: Complete Reference" },
      { slug: "facebook-business-manager-disabled-complete-reference", title: "Facebook BM Disabled: Complete Reference" },
    ],
  },
};
